import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { loadVhuBids, loadVhuLots } from "./vhuLotService";

const bidStatusColor = {
  GAGNANTE: "success",
  EN_COURS: "primary",
  DEPASSEE: "secondary",
  ANNULEE: "danger",
};

const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;
const boolLabel = (value) => (value ? "Oui" : "Non");
const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function VhuBidOffersPage() {
  const [lots, setLots] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lotFilter, setLotFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const [lotResponse, bidResponse] = await Promise.all([
        loadVhuLots(),
        loadVhuBids(),
      ]);
      setLots(lotResponse?.data?.body || []);
      setBids(bidResponse?.data?.body || []);
    } finally {
      setLoading(false);
    }
  };

  const visibleBids = useMemo(() => {
    const search = query.trim().toLowerCase();
    return bids.filter((bid) => {
      const matchesLot = !lotFilter || String(bid.lotId) === String(lotFilter);
      const matchesStatus = !statusFilter || bid.status === statusFilter;
      const matchesQuery = !search || [
        bid.ferrailleurNom,
        bid.lotReference,
        bid.lotTitre,
        bid.commentaire,
      ].filter(Boolean).join(" ").toLowerCase().includes(search);
      return matchesLot && matchesStatus && matchesQuery;
    });
  }, [bids, lotFilter, statusFilter, query]);

  return (
    <div className="page-content auction-page vhu-lots-page vhu-offers-page">
      <Container fluid>
        <Breadcrumbs title="Offres VHU" breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Offres VHU", link: "#" }]} />

        <Card className="mb-4 vhu-list-toolbar">
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col lg={4}>
                <Label>Lot</Label>
                <Input type="select" value={lotFilter} onChange={(event) => setLotFilter(event.target.value)}>
                  <option value="">Tous les lots</option>
                  {lots.map((lot) => (
                    <option key={lot.id} value={lot.id}>{lot.reference || lot.titre || `Lot #${lot.id}`}</option>
                  ))}
                </Input>
              </Col>
              <Col lg={3}>
                <Label>Statut</Label>
                <Input type="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">Tous les statuts</option>
                  <option value="GAGNANTE">Gagnante</option>
                  <option value="EN_COURS">En cours</option>
                  <option value="DEPASSEE">Depassee</option>
                  <option value="ANNULEE">Annulee</option>
                </Input>
              </Col>
              <Col lg={5}>
                <Label>Recherche</Label>
                <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Acheteur, lot, commentaire..." />
              </Col>
            </Row>
          </CardBody>
        </Card>

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : !visibleBids.length ? (
          <div className="vhu-empty-state">
            <i className="ri-auction-line" />
            <strong>Aucune offre a afficher</strong>
            <span>Les offres envoyees depuis le mobile apparaitront ici.</span>
          </div>
        ) : (
          <Card className="vhu-bids-panel">
            <CardBody>
              <div className="vhu-bid-list">
                {visibleBids.map((bid) => (
                  <div className={bid.isBestBid ? "vhu-bid-row best" : "vhu-bid-row"} key={bid.id}>
                    <div className="vhu-bid-main">
                      <div className="vhu-bid-heading">
                        <div>
                          <strong>{bid.ferrailleurNom || "Acheteur"}</strong>
                          <span>{bid.lotReference || bid.lotTitre || `Lot #${bid.lotId}`}</span>
                        </div>
                        <Badge color={bidStatusColor[bid.status] || "secondary"}>{bid.status || "-"}</Badge>
                      </div>
                      <div className="vhu-bid-details">
                        <span><b>Montant</b>{moneyLabel(bid.montant)}</span>
                        <span><b>Date offre</b>{dateLabel(bid.dateOffre)}</span>
                        <span><b>Delai paiement</b>{bid.delaiPaiement || "-"}</span>
                        <span><b>Delai enlevement</b>{bid.delaiEnlevement || "-"}</span>
                        <span><b>Moyen paiement</b>{bid.moyenPaiementPrevu || "-"}</span>
                        <span><b>Conditions</b>{boolLabel(bid.termsAccepted)}</span>
                        <span><b>Offre ferme</b>{boolLabel(bid.firmOfferAccepted)}</span>
                      </div>
                      {bid.commentaire && (
                        <div className="vhu-bid-comment">
                          <b>Commentaire</b>
                          <span>{bid.commentaire}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
}

export default VhuBidOffersPage;
